/**
 * Coordinates dashboard calendar + pending-task freshness after booking mutations.
 * Uses a monotonic session revision so returning to /dashboard/ always beats stale cache.
 */

import { invalidateDashboardDataCache } from './dashboardDataCache';

const REVISION_STORAGE_KEY = 'roomnexa:dashboard-data-revision';
const REVISION_EVENT = 'roomnexa:dashboard-data-revision';

export function readDashboardDataRevision() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = Number(window.sessionStorage.getItem(REVISION_STORAGE_KEY));
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
  } catch {
    return 0;
  }
}

export function bumpDashboardDataRevision(reason = 'booking') {
  if (typeof window === 'undefined') return 0;

  const next = readDashboardDataRevision() + 1;
  try {
    window.sessionStorage.setItem(REVISION_STORAGE_KEY, String(next));
  } catch {
    // ignore
  }

  invalidateDashboardHomeCaches();

  window.dispatchEvent(
    new CustomEvent(REVISION_EVENT, {
      detail: { revision: next, reason },
    }),
  );

  return next;
}

export function invalidateDashboardHomeCaches() {
  invalidateDashboardDataCache('calendar:');
  invalidateDashboardDataCache('pending-tasks:');
}

export function shouldRefreshDashboardData(appliedRevision = 0) {
  const current = readDashboardDataRevision();
  const loaded = readDashboardLoadedRevision();
  if (current <= loaded) {
    return false;
  }
  return current > Number(appliedRevision || 0);
}

const LOADED_REVISION_KEY = 'roomnexa:dashboard-loaded-revision';

export function readDashboardLoadedRevision() {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = Number(window.sessionStorage.getItem(LOADED_REVISION_KEY));
    return Number.isFinite(raw) && raw >= 0 ? raw : 0;
  } catch {
    return 0;
  }
}

export function markDashboardDataLoaded(revision = readDashboardDataRevision()) {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(LOADED_REVISION_KEY, String(revision));
  } catch {
    // ignore
  }
}

export function dashboardNeedsRefresh() {
  return shouldRefreshDashboardData(readDashboardLoadedRevision());
}

export function subscribeDashboardDataRevision(callback) {
  if (typeof window === 'undefined') return () => {};

  const handler = (event) => {
    callback(event.detail?.revision ?? readDashboardDataRevision(), event.detail || {});
  };

  window.addEventListener(REVISION_EVENT, handler);
  return () => window.removeEventListener(REVISION_EVENT, handler);
}
